import React, { useCallback, useRef, useEffect, useState } from "react";
import { Label } from "../../ui/label";
import { Input } from "../../ui/input";
import { Button } from "../../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { RichTextEditor } from "../RichTextEditor";
import {
  AutomatedControlData,
  OperatingEffectivenessStep,
} from "../types-automated";
import { NATURE_OF_PROCEDURE_OPTIONS } from "../types";
import { Plus, Trash2 } from "lucide-react";

interface OperatingEffectivenessAutomatedProps {
  data: AutomatedControlData;
  onChange: (data: Partial<AutomatedControlData>) => void;
  controlId?: string;
}

export const OperatingEffectivenessAutomated: React.FC<OperatingEffectivenessAutomatedProps> = ({ data, onChange }) => {
  // Use ref to access latest onChange without causing callback recreation
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const addProcedure = useCallback(() => {
    const newProcedure: OperatingEffectivenessStep = {
      id: `proc-${Date.now()}`,
      stepDescription: "",
      natureOfProcedure: "",
      procedureDescription: "",
    };
    onChangeRef.current({ operatingProcedures: [...data.operatingProcedures, newProcedure] });
  }, [data.operatingProcedures]);

  const removeProcedure = useCallback((id: string) => {
    onChangeRef.current({ operatingProcedures: data.operatingProcedures.filter((p) => p.id !== id) });
  }, [data.operatingProcedures]);

  const updateProcedure = useCallback((id: string, field: keyof OperatingEffectivenessStep, value: string) => {
    onChangeRef.current({
      operatingProcedures: data.operatingProcedures.map((p) =>
        p.id === id ? { ...p, [field]: value } : p
      ),
    });
  }, [data.operatingProcedures]);

  const handleUsingWorkOfOthersChange = useCallback((value: string) => {
    onChangeRef.current({ usingWorkOfOthers: value as "Yes" | "No" | "" });
  }, []);

  const handleWorkOfOthersApproachChange = useCallback((value: string) => {
    onChangeRef.current({ workOfOthersApproach: value as "Selected For Reperformance" | "Not Selected For Reperformance" | "" });
  }, []);

  const handleIsInformationUsedChange = useCallback((value: string) => {
    onChangeRef.current({ isInformationUsed: value as "Yes" | "No" | "" });
  }, []);

  const handleIsOperatingEffectiveChange = useCallback((value: string) => {
    onChangeRef.current({ isOperatingEffective: value as "Yes" | "No" | "Not Yet Concluded" });
  }, []);

  // Show "Not Applicable" if testOperatingEffectiveness is false
  if (!data.testOperatingEffectiveness) {
    return (
      <div className="bg-white/5 border border-white/10 rounded-lg">
        <div className="border-b border-white/10 p-6">
          <h2 className="text-xl font-semibold text-white">Operating Effectiveness</h2>
        </div>
        <div className="p-6">
          <div className="flex items-center justify-center py-12">
            <p className="text-lg text-white/60">Not Applicable</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white/5 border border-white/10 rounded-lg">
      <div className="border-b border-white/10 p-6">
        <h2 className="text-xl font-semibold text-white">Operating Effectiveness</h2>
      </div>
      <div className="p-6 space-y-8">
        {/* Timing & Extent of procedures */}
        <div className="space-y-6">
          <h3 className="text-lg font-medium text-white">i. Timing & Extent of procedures</h3>

          {/* Timing of Procedure - Always "Conclude at Interim" for automated */}
          <div className="space-y-2">
            <Label className="text-white">What is the timing of procedure?</Label>
            <div className="p-4 border border-white/10 rounded-lg bg-black/20">
              <p className="text-white">
                Conclude at Interim (Perform a complete test of the control at an interim date. This approach requires us to perform sufficient testing to enable us to reach a preliminary conclusion regarding the operating effectiveness of controls tested at interim date)
              </p>
            </div>
          </div>

          {/* Extent of Procedures */}
          <div className="space-y-4 p-4 border border-white/10 rounded-lg bg-black/20">
            <h4 className="text-sm font-semibold text-white">Extent of Procedures</h4>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white">Particulars</Label>
                </div>
                <div>
                  <Label className="text-white">Assessment</Label>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white">Risk of Material Misstatement</Label>
                </div>
                <div>
                  <Input
                    value={data.riskOfMaterialMisstatement}
                    onChange={(e) => onChangeRef.current({ riskOfMaterialMisstatement: e.target.value })}
                    className="bg-black/20 border-white/10 text-white"
                    placeholder="Enter assessment"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white">Risk Associated with Control</Label>
                </div>
                <div>
                  <Input
                    value={data.riskAssociatedWithControl}
                    onChange={(e) => onChangeRef.current({ riskAssociatedWithControl: e.target.value })}
                    className="bg-black/20 border-white/10 text-white"
                    placeholder="Enter assessment"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white">Sample size</Label>
                </div>
                <div>
                  <Input
                    value={data.sampleSize}
                    onChange={(e) => onChangeRef.current({ sampleSize: e.target.value })}
                    className="bg-black/20 border-white/10 text-white"
                    placeholder="Enter sample size"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2 mt-4">
              <Label className="text-white">Document rationale for sample size.</Label>
              <RichTextEditor
                value={data.sampleSizeRationale}
                onChange={(content) => onChangeRef.current({ sampleSizeRationale: content })}
                placeholder="Enter rationale..."
              />
            </div>
          </div>

          {/* Using the work of others */}
          <div className="space-y-2">
            <Label className="text-white">Using the work of others?</Label>
            <Select
              value={data.usingWorkOfOthers || ""}
              onValueChange={handleUsingWorkOfOthersChange}
            >
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Select Yes/No" />
              </SelectTrigger>
              <SelectContent className="bg-black/90 border-white/10">
                <SelectItem value="Yes">Yes</SelectItem>
                <SelectItem value="No">No</SelectItem>
              </SelectContent>
            </Select>

            {data.usingWorkOfOthers === "Yes" && (
              <div className="mt-4 space-y-2">
                <Label className="text-white">Approach to testing the work performed by others is</Label>
                <Select
                  value={data.workOfOthersApproach || ""}
                  onValueChange={handleWorkOfOthersApproachChange}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select approach" />
                  </SelectTrigger>
                  <SelectContent className="bg-black/90 border-white/10">
                    <SelectItem value="Selected For Reperformance">Selected For Reperformance</SelectItem>
                    <SelectItem value="Not Selected For Reperformance">Not Selected For Reperformance</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Is information used in testing operating effectiveness */}
          <div className="space-y-2">
            <Label className="text-white">Is information used in testing operating effectiveness?</Label>
            <Select
              value={data.isInformationUsed || ""}
              onValueChange={handleIsInformationUsedChange}
            >
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Select Yes/No" />
              </SelectTrigger>
              <SelectContent className="bg-black/90 border-white/10">
                <SelectItem value="Yes">Yes</SelectItem>
                <SelectItem value="No">No</SelectItem>
              </SelectContent>
            </Select>
            {data.isInformationUsed === "Yes" && (
              <p className="text-sm text-white/60 mt-2">
                Option to associate information (more details later)
              </p>
            )}
          </div>
        </div>

        {/* Nature of Procedures */}
        <div className="space-y-6 border-t border-white/10 pt-6">
          <h3 className="text-lg font-medium text-white">ii. Nature of Procedures</h3>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-white">
                Procedures performed to evaluate the operating effectiveness of control.
              </Label>
              <Button
                onClick={addProcedure}
                className="bg-primary text-white hover:bg-primary/80"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Procedure
              </Button>
            </div>

            {data.operatingProcedures.length === 0 ? (
              <div className="p-4 border border-white/10 rounded-lg bg-black/20 space-y-4">
                <div className="space-y-2">
                  <Label className="text-white">Nature of testing procedure</Label>
                  <Select
                    value=""
                    onValueChange={(value) => {
                      const newProcedure: OperatingEffectivenessStep = {
                        id: `proc-default-${Date.now()}`,
                        stepDescription: "",
                        natureOfProcedure: value,
                        procedureDescription: "",
                      };
                      onChangeRef.current({ operatingProcedures: [newProcedure] });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select nature of procedure" />
                    </SelectTrigger>
                    <SelectContent className="bg-black/90 border-white/10">
                      {NATURE_OF_PROCEDURE_OPTIONS.map((option) => (
                        <SelectItem key={option} value={option}>
                          {option}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <RichTextEditor
                  label="Procedure Description"
                  value=""
                  onChange={(content) => {
                    const newProcedure: OperatingEffectivenessStep = {
                      id: `proc-default-${Date.now()}`,
                      stepDescription: "",
                      natureOfProcedure: "",
                      procedureDescription: content,
                    };
                    onChangeRef.current({ operatingProcedures: [newProcedure] });
                  }}
                  placeholder="Describe the procedure..."
                />

                <RichTextEditor
                  label="Test of operating effectiveness"
                  value=""
                  onChange={() => {}}
                  placeholder="Document procedures performed..."
                />
              </div>
            ) : (
              <div className="space-y-4">
                {data.operatingProcedures.map((procedure, index) => (
                  <div key={procedure.id} className="p-4 border border-white/10 rounded-lg bg-black/20 space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-semibold text-white">
                        Procedure {index + 1}
                      </h4>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeProcedure(procedure.id)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-white">Nature of testing procedure</Label>
                      <Select
                        value={procedure.natureOfProcedure || ""}
                        onValueChange={(value) => updateProcedure(procedure.id, "natureOfProcedure", value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select nature of procedure" />
                        </SelectTrigger>
                        <SelectContent className="bg-black/90 border-white/10">
                          {NATURE_OF_PROCEDURE_OPTIONS.map((option) => (
                            <SelectItem key={option} value={option}>
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <RichTextEditor
                      label="Procedure Description"
                      value={procedure.procedureDescription}
                      onChange={(content) => updateProcedure(procedure.id, "procedureDescription", content)}
                      placeholder="Describe the procedure..."
                    />

                    <RichTextEditor
                      label="Test of operating effectiveness"
                      value={procedure.stepDescription}
                      onChange={(content) => updateProcedure(procedure.id, "stepDescription", content)}
                      placeholder="Document procedures performed..."
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Operating Effectiveness Testing Conclusion */}
        <div className="space-y-4 border-t border-white/10 pt-6">
          <h3 className="text-lg font-medium text-white">iii. Operating Effectiveness Testing Conclusion</h3>

          <div className="space-y-2">
            <Label className="text-white">Is the control operating effectively?</Label>
            <Select
              value={data.isOperatingEffective || "Not Yet Concluded"}
              onValueChange={handleIsOperatingEffectiveChange}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-black/90 border-white/10">
                <SelectItem value="Yes">Yes</SelectItem>
                <SelectItem value="No">No</SelectItem>
                <SelectItem value="Not Yet Concluded">Not Yet Concluded</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
    </div>
  );
};

